/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Typography2, breakpoints } from '@coursera/cds-core';

import { initiateLoginFlow } from 'bundles/authentication-common/components/authenticationModal/OpenAI/utils';
import UnifiedAuthEntryForm from 'bundles/authentication-common/components/forms/UnifiedAuthEntryForm';
import {
  AUTHENTICATION_FORM_VARIANTS,
  AUTHENTICATION_MODAL_MODES,
  type AUTHENTICATION_MODAL_MODES_KEY,
  type LoginMethodType,
} from 'bundles/authentication-common/constants';
import type { EnterpriseSsoLoginInfo } from 'bundles/authentication-common/hooks/useEnterpriseSsoCheck';
import type { ThirdPartyOrganizationsData } from 'bundles/authentication-common/hooks/useUserThirdPartyOrganizationsQuery';
import { loginMethodsToAuthModeWithMetadata } from 'bundles/authentication-common/utils/navigationUtils';

import _t from 'i18n!nls/authentication';

export type Props = ThirdPartyOrganizationsData & {
  className?: string;
  navigate: (
    path: AUTHENTICATION_MODAL_MODES_KEY,
    options?: Record<string, unknown>,
    shouldSwitchFormsByQueryParam?: boolean
  ) => void;
  enterpriseSsoEmailCheckInfo: {
    enterpriseSsoLoginInfo: EnterpriseSsoLoginInfo;
    checkForceSSO: (email: string) => Promise<void>;
    onContinueWithSso: () => void;
  };
  title?: string;
  tagline?: string;
  authMode?: AUTHENTICATION_MODAL_MODES_KEY;
  redirect?: string;
};

const styles = {
  container: css`
    display: grid;
    grid-template-columns: 1fr;
    margin: 0 auto;
    max-width: 396px;
    padding: 0;

    @media (min-width: 600px) {
      padding: var(--cds-spacing-50);
    }

    .cds-text-field-root {
      margin-top: var(--cds-spacing-150) !important;
    }

    ${breakpoints.down('xs')} {
      padding: var(--cds-spacing-300) var(--cds-spacing-200);
    }
  `,
  tagLine: css`
    margin: var(--cds-spacing-50) auto 0;
    width: 100%;
    padding: 0 var(--cds-spacing-400);
    text-align: center !important;
  `,
  title: css`
    margin: 0;
    padding: 0 var(--cds-spacing-600);
    margin-bottom: var(--cds-spacing-200);
    text-align: center !important;
  `,
  link: css`
    text-decoration: underline;
  `,
  organization: css`
    margin: var(--cds-spacing-200) 0 var(--cds-spacing-150) 0;
  `,
  terms: css`
    display: none;
    margin: var(--cds-spacing-150) 0 0;
    padding: 0;

    &:first-of-type {
      margin: 0;
    }

    &:nth-of-type(n) a {
      color: inherit;
      text-decoration: underline;
    }
  `,
  header: css`
    margin-bottom: var(--cds-spacing-150);
  `,
  form: css`
    form {
      gap: var(--cds-spacing-400);
    }

    .cds-input-input::placeholder {
      color: var(--cds-color-neutral-primary-weak);
    }

    .cds-input-input {
      padding: var(--cds-spacing-100);
    }

    .cds-formLabel-indicatorLabel {
      display: none;
    }
  `,
};

const OpenAIAuth = ({
  navigate,
  className,
  title,
  tagline,
  authMode,
  enterpriseSsoEmailCheckInfo,
  redirect,
  organizations,
}: Props) => {
  const { checkForceSSO } = enterpriseSsoEmailCheckInfo;
  const [submission, setSubmission] = useState<null | {
    loginMethods: Array<LoginMethodType>;
    email: string;
  }>(null);

  useEffect(() => {
    // from authProvider query param
    const organizationId = organizations?.[0]?.id;
    if (organizationId) {
      initiateLoginFlow(organizationId, redirect);
      return;
    }

    const { isLoading, isEnterpriseSsoLogin } = enterpriseSsoEmailCheckInfo.enterpriseSsoLoginInfo;

    if (submission) {
      if (isEnterpriseSsoLogin) {
        if (isLoading) {
          return;
        }

        // from forced SSO
        enterpriseSsoEmailCheckInfo.onContinueWithSso();
        return;
      }

      const { loginMethods, email } = submission;

      // rest of the login flows
      const {
        mode,
        navigationOptions: options,
        shouldSwitchFormsByQueryParam,
      } = loginMethodsToAuthModeWithMetadata({ loginMethods, email });

      navigate(mode, options, shouldSwitchFormsByQueryParam);
    }
  }, [enterpriseSsoEmailCheckInfo, submission, navigate, organizations, redirect]);

  const handleOnSuccess = useCallback(
    async ({ loginMethods, email }: { loginMethods: Array<LoginMethodType>; email: string }) => {
      setSubmission({ loginMethods, email });
    },
    []
  );

  const handleOnError = (email: string) => {
    navigate(
      authMode === AUTHENTICATION_MODAL_MODES.SIGNUP
        ? AUTHENTICATION_MODAL_MODES.SIGNUPCONTINUE
        : AUTHENTICATION_MODAL_MODES.LOGINCONTINUE,
      { defaultEmail: email, formVariant: AUTHENTICATION_FORM_VARIANTS.PASSWORD },
      false
    );
  };

  return (
    <section css={styles.container} className={className} data-catchpoint="authentication-box-content">
      <header css={styles.header}>
        <Typography2 component="h1" variant="titleXSmall" align="left" css={styles.title}>
          {title ?? _t('Log in or create your Coursera account')}
        </Typography2>
        <Typography2 component="p" variant="bodyPrimary" color="supportText" align="left" css={styles.tagLine}>
          {tagline ?? <FormattedMessage message={_t('Learn at your own pace with OpenAI Certified courses.')} />}
        </Typography2>
      </header>
      <div css={styles.form}>
        <UnifiedAuthEntryForm onSuccess={handleOnSuccess} checkForceSSO={checkForceSSO} onError={handleOnError} />
      </div>
      <div className="auth-form-tos-section">
        <Typography2 component="p" variant="bodyTertiary" color="supportText" align="left" css={styles.terms}>
          <FormattedMessage
            message={_t(
              'This site is protected by reCAPTCHA Enterprise and the Google {privacyPolicyLink} and {tosLink} apply.'
            )}
            privacyPolicyLink={
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                {_t('Privacy Policy')}
              </a>
            }
            tosLink={
              <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
                {_t('Terms of Service')}
              </a>
            }
          />
        </Typography2>
      </div>
    </section>
  );
};

export default OpenAIAuth;
